// Helpers for deriving doubles team labels from picked players.
//
// Conventions match the editorial flow in PlanTeamCreation.md:
//   "Surname Initial / Surname Initial" — e.g. "Nadal R / Djokovic N".
//
// Player names from the API arrive in either order:
//   "Nadal, Rafael"  (last-comma-first — most common from SR)
//   "Rafael Nadal"   (first-then-last)
// Single-token or blank input is handled gracefully — never throw.

export function derivePlayerLabel(name: string | null | undefined): string {
  if (!name) return ''
  const trimmed = name.trim()
  if (!trimmed) return ''

  if (trimmed.includes(',')) {
    const [last, first] = trimmed.split(',', 2).map(s => s.trim())
    return first ? `${last} ${first[0]?.toUpperCase() ?? ''}`.trim() : last
  }

  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) return parts[0]
  const surname = parts[parts.length - 1]
  const initial = parts[0][0]?.toUpperCase() ?? ''
  return initial ? `${surname} ${initial}` : surname
}

export function deriveTeamName(
  p1?: string | null,
  p2?: string | null,
): string {
  const a = derivePlayerLabel(p1)
  const b = derivePlayerLabel(p2)
  if (!a || !b) return ''
  return `${a} / ${b}`
}

// Build a "X/Y" abbreviation from picked players, where each part is the
// first letter of the player's surname. e.g. Nadal / Shelton -> "N/S".
// Editors can override the auto-derived value.
function deriveAbbrPart(fullName: string | null | undefined): string {
  const label = derivePlayerLabel(fullName)
  if (!label) return ''
  // derivePlayerLabel produces "Surname X" or just "Surname"; we want the
  // first character of the surname.
  const surname = label.split(' ')[0]
  return surname[0]?.toUpperCase() ?? ''
}

export function deriveTeamAbbreviation(
  p1: { name?: string | null } | null,
  p2: { name?: string | null } | null,
): string {
  const a = p1 ? deriveAbbrPart(p1.name) : ''
  const b = p2 ? deriveAbbrPart(p2.name) : ''
  if (!a || !b) return ''
  return `${a}/${b}`
}
